import React, { useEffect, useState } from "react";
import classes from "../memory.module.css";
import CustomModal from "../../../components/Modals/CustomModal";
import TypographyInline from "../../../components/Typography/TypographyInline";
import {
  BASE,
  ERROR,
  GREY,
  PRIMARY,
  SUCCESS,
  WARNING,
} from "../../../utils/constants/colors";
import { fontWeight } from "../../../utils/constants/UI";
import Button from "../../../components/Buttons/Button";

const MemoryProcessingModal = ({
  open,
  status,
  sourceLabel,
  elapsedLabel,
  stages,
  currentStageIndex,
  progress,
  tips,
  errorState,
  firebaseMessage,
  onClose,
  onRetry,
  onSwitchSource,
}) => {
  const [tipIndex, setTipIndex] = useState(0);
  const isTerminal = status === "failed" || status === "timeout";
  const isCompleted = status === "completed";
  const activeStageLabel = stages[currentStageIndex] || "Preparing job";

  useEffect(() => {
    if (!open || !tips?.length || isTerminal || isCompleted) return undefined;

    const intervalId = setInterval(() => {
      setTipIndex((current) => (current + 1) % tips.length);
    }, 4000);

    return () => clearInterval(intervalId);
  }, [isCompleted, isTerminal, open, tips]);

  // Determine the gradient color for the ring
  const ringColor = isTerminal
    ? ERROR.W_500
    : isCompleted
      ? SUCCESS.W_600
      : PRIMARY.W_500;

  const ringTrack = isTerminal ? "#fce4e4" : isCompleted ? "#dcefe2" : "#e8edf4";

  // SVG circle progress
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const strokeOffset = circumference - (progress / 100) * circumference;

  return (
    <CustomModal
      width="420px"
      blurBackdrop={true}
      open={open}
      onClose={onClose}
      header={{
        heading: {
          label: isTerminal
            ? "Generation Failed"
            : "Generating Knowledge",
        },
      }}
      footer={{
        right: (
          <div className={classes.actionButtons}>
            <Button
              label={isTerminal ? "Close" : "Hide"}
              variant="ghost"
              size="sm36"
              onClick={onClose}
            />
          </div>
        ),
      }}
    >
      <div className={classes.processingModalBody}>
        {/* Circular progress hero */}
        <div className={classes.processingRingSection}>
          <div className={classes.processingRingWrapper}>
            <svg
              width="132"
              height="132"
              viewBox="0 0 132 132"
              className={classes.processingRingSvg}
            >
              <circle
                cx="66"
                cy="66"
                r={radius}
                fill="none"
                stroke={ringTrack}
                strokeWidth="8"
              />
              <circle
                cx="66"
                cy="66"
                r={radius}
                fill="none"
                stroke={ringColor}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeOffset}
                className={classes.processingRingProgress}
              />
            </svg>
            <div className={classes.processingRingCenter}>
              <span className={classes.processingRingPercent}>
                {progress}%
              </span>
            </div>
          </div>

          <div className={classes.processingRingLabel}>
            <TypographyInline
              label={
                isTerminal
                  ? "Something went wrong"
                  : firebaseMessage || activeStageLabel
              }
              size="sm"
              fontWeight={fontWeight.SEMIBOLD}
              color={isTerminal ? ERROR.W_600 : BASE.BLACK}
            />
            <TypographyInline
              label={`${sourceLabel} · ${elapsedLabel}`}
              size="xs"
              fontWeight={fontWeight.REGULAR}
              color={GREY.SECONDARY}
            />
          </div>
        </div>

        {/* Stage steps - compact */}
        {!isTerminal && (
          <div className={classes.processingSteps}>
            {stages.map((stage, index) => {
              const isDone = index < currentStageIndex;
              const isActive = index === currentStageIndex;

              return (
                <div
                  key={stage}
                  className={`${classes.processingStep} ${
                    isDone ? classes.processingStepDone : ""
                  } ${isActive ? classes.processingStepActive : ""}`}
                >
                  <span className={classes.processingStepDot}>
                    {isDone ? (
                      <svg width="10" height="10" viewBox="0 0 10 10">
                        <path
                          d="M2 5l2.5 2.5L8 3"
                          stroke="white"
                          strokeWidth="1.5"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : null}
                  </span>
                  <TypographyInline
                    label={stage}
                    size="xs"
                    fontWeight={
                      isActive ? fontWeight.SEMIBOLD : fontWeight.REGULAR
                    }
                    color={
                      isDone
                        ? SUCCESS.W_700
                        : isActive
                          ? BASE.BLACK
                          : GREY.TERTIARY
                    }
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Error state */}
        {isTerminal && (
          <div className={classes.processingFailure}>
            <TypographyInline
              label={
                errorState?.reason ||
                "The AI service could not complete this request."
              }
              size="sm"
              fontWeight={fontWeight.MEDIUM}
              color={ERROR.W_500}
            />
            {errorState?.recommendation && (
              <TypographyInline
                label={errorState.recommendation}
                size="xs"
                fontWeight={fontWeight.REGULAR}
                color={GREY.SECONDARY}
              />
            )}
            <div className={classes.processingFailureActions}>
              {onRetry && (
                <Button
                  label="Try Again"
                  variant="primary"
                  size="sm36"
                  onClick={onRetry}
                />
              )}
              <Button
                label="Switch Source"
                variant="ghost"
                size="sm36"
                onClick={() => onSwitchSource("pdf")}
              />
            </div>
          </div>
        )}

        {/* Tip */}
        {!isTerminal && tips?.[tipIndex] && (
          <div className={classes.processingTip}>
            <TypographyInline
              label={tips[tipIndex]}
              size="xs"
              fontWeight={fontWeight.REGULAR}
              color={WARNING.W_700}
            />
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default MemoryProcessingModal;
